import { useTranslation } from 'react-i18next'

import { Box, Checkbox, Flex, Link, Text } from '@enechain/ecloud-designsystem'
import { FaArrowUpRightFromSquareRegular } from '@enechain/ecloud-icons'

import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { WEEKDAY_KEYS } from '~/components/common/DayGrid'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
} from '~/components/common/filters'
import { iconSize } from '~/constants/iconSize'
import { civilDateToInputValue, parseDateInputValue } from '~/models/civilDate'
import { formatDateDisplay } from '~/models/dateTime'
import type { AreaDef } from '~/pages/SpotPrice/constants'
import { AREAS } from '~/pages/SpotPrice/constants'

/** `CompactCivilDateField` の id (label 経由の accessible name / test の入口)。 */
const DATE_FROM_FIELD_ID = 'spot-price-delivery-date-from'
const DATE_TO_FIELD_ID = 'spot-price-delivery-date-to'

type Props = {
  dateFrom: string
  dateTo: string
  maxDate: string
  onDateFromChange: (v: string) => void
  onDateToChange: (v: string) => void
}

/** 開閉の保存キー (CollapsibleFilterToolbar が接頭辞を足す)。 */
const SPOT_PRICE_FILTER_STORAGE_KEY = 'spotPrice'

/**
 * スポット価格の絞り込み (開閉式。UI 辞書 toolbar-collapsible.md「開閉式」)。
 *
 * プロトタイプ (Vercel) = React mock `pages/SpotPrice/components/FilterToolbar.tsx` と同じ形:
 * 開いたカードと閉じた時の要約に入れるのは受渡日だけ。受渡日は見るデータの日を替える日付で、既定に戻す
 * 条件を持たないので「クリア」は押せない (件数も常に 0)。エリアは系列の表示の切り替えなので
 * カードの外の行 ([[AreaToggleRow]])、データソース (外部リンク) は絞り込みではないので画面の右上
 * ([[SpotPriceDataSourceLink]])。閉じたカードにエリアを入れると、グラフの系列を切り替えるたびに開くことになるため。
 */
export const FilterToolbar = ({
  dateFrom,
  dateTo,
  maxDate,
  onDateFromChange,
  onDateToChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // `maxDate` は文字列 (YYYY-MM-DD)。 popup 側は CivilDate を受けるので変換する。
  // 未来日を選ばせない上限は index.tsx が同じ値で clamp 済みなので、 ここは
  // **popup 上でも選べなくする**ための可視化 (issue #6361 で `max` が入った)。
  const maxCivilDate = parseDateInputValue(maxDate)

  // 要約の日付は日付欄 (CompactCivilDateField) の閉じた表示と同じ `YYYY/MM/DD (曜)` (時間前入札の要約と同じ作り)
  const summaryDate = (value: string): string => {
    const date = new Date(`${value}T00:00:00`)
    if (Number.isNaN(date.getTime())) return value
    return t('common:date.withWeekday', {
      date: formatDateDisplay(date),
      weekday: t(`common:date.calendar.weekday.${WEEKDAY_KEYS[date.getDay()]}`),
    })
  }
  const deliveryDateLabel = t('page:spotPrice.filter.deliveryDate')

  return (
    <CollapsibleFilterToolbar
      modifiedCount={0}
      storageKey={SPOT_PRICE_FILTER_STORAGE_KEY}
      summary={[
        {
          key: 'deliveryDate',
          label: deliveryDateLabel,
          value: `${summaryDate(dateFrom)}〜${summaryDate(dateTo)}`,
        },
      ]}
    >
      <FilterGrid
        items={[
          {
            key: 'deliveryDate',
            label: deliveryDateLabel,
            control: (
              <Flex alignItems="center" gap={1}>
                <Box width={COMPACT_DATE_FIELD_WIDTH}>
                  <CompactCivilDateField
                    aria-label={t('page:spotPrice.filter.deliveryDateFrom')}
                    id={DATE_FROM_FIELD_ID}
                    max={maxCivilDate}
                    onChange={(d) => {
                      // 空欄化 (キーボードで消した時) は既存挙動と揃えて無視する
                      // — 受渡日は必須で、 未入力にすると一覧の問い合わせが組めない。
                      if (d !== undefined)
                        onDateFromChange(civilDateToInputValue(d))
                    }}
                    value={parseDateInputValue(dateFrom)}
                  />
                </Box>
                {/* 8080 は文字サイズを継承 (14px) */}
                <Text color="Text.midEmphasis">～</Text>
                <Box width={COMPACT_DATE_FIELD_WIDTH}>
                  <CompactCivilDateField
                    aria-label={t('page:spotPrice.filter.deliveryDateTo')}
                    id={DATE_TO_FIELD_ID}
                    max={maxCivilDate}
                    onChange={(d) => {
                      if (d !== undefined)
                        onDateToChange(civilDateToInputValue(d))
                    }}
                    value={parseDateInputValue(dateTo)}
                  />
                </Box>
              </Flex>
            ),
          },
        ]}
      />
    </CollapsibleFilterToolbar>
  )
}

type AreaToggleRowProps = {
  visibleAreas: Set<string>
  onAreaToggle: (code: string, checked: boolean) => void
  onMasterToggle: (checked: boolean) => void
}

/**
 * グラフ・表に出すエリアの切り替え (開閉式のカードの下の行。React mock の「エリア」の行と同じ置き方)。
 * 絞り込みではなく系列の表示の切り替えなので、カードの要約・件数・クリアには入れない。
 */
export const AreaToggleRow = ({
  visibleAreas,
  onAreaToggle,
  onMasterToggle,
}: AreaToggleRowProps): React.ReactElement => {
  const { t } = useTranslation()

  // 全エリア master = 9 エリア (system 除く) が全 ON かどうかで indeterminate/checked を決める
  const nonSystemAreas = AREAS.filter((a) => !a.isSystem)
  const checkedCount = nonSystemAreas.filter((a) =>
    visibleAreas.has(a.code),
  ).length
  const allChecked = checkedCount === nonSystemAreas.length
  const someChecked = checkedCount > 0 && !allChecked

  return (
    <Flex
      alignItems="center"
      flexWrap="wrap"
      gap="8px 16px"
      aria-label={t('page:spotPrice.filter.area')}
      role="group"
    >
      <Text
        textStyle="filterLabel"
        flexShrink={0}
        color="Text.highEmphasis"
        whiteSpace="nowrap"
      >
        {t('page:spotPrice.filter.area')}
      </Text>
      {/* 全エリア master checkbox (system 除く) */}
      <Checkbox
        checked={someChecked ? 'indeterminate' : allChecked}
        onCheckedChange={(detail: { checked: boolean | 'indeterminate' }) => {
          onMasterToggle(detail.checked === true)
        }}
      >
        {t('page:spotPrice.filter.allAreas')}
      </Checkbox>

      {/* システムプライス (divider で分離・master 対象外) */}
      {AREAS.filter((a) => a.isSystem).map((a: AreaDef) => (
        <AreaCheckbox
          key={a.code}
          area={a}
          checked={visibleAreas.has(a.code)}
          onToggle={onAreaToggle}
        />
      ))}

      {/* divider (8080 `.ecl-toolbar-divider` = 1px × 20px) */}
      <Box
        width="1px"
        height="20px"
        aria-hidden="true"
        backgroundColor="Border.lowEmphasis"
      />

      {/* 9 エリア */}
      {nonSystemAreas.map((a: AreaDef) => (
        <AreaCheckbox
          key={a.code}
          area={a}
          checked={visibleAreas.has(a.code)}
          onToggle={onAreaToggle}
        />
      ))}
    </Flex>
  )
}

/** データソース (JEPX の外部リンク)。絞り込みではないので画面の右上 (PageLayout の headerExtra) に置く。 */
export const SpotPriceDataSourceLink = (): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <Flex alignItems="center" gap={2} whiteSpace="nowrap">
      <Text color="Text.midEmphasis">
        {t('page:spotPrice.filter.dataSource')}
      </Text>
      <Link
        href="https://www.jepx.jp/electricpower/market-data/spot/"
        rel="noopener noreferrer"
        target="_blank"
      >
        {/* 出典リンク文言は API 由来データではなく静的な UI ラベルなので i18n
              経由にする (旧「i18n 対象外」コメントは誤り)。ja/en 両方を定義済み。
              SpotPrice ページの他のフィルタ文言 (dataSource 等) の en 化は
              未着手で、#6831 の残タスクとして ja へフォールバックする。 */}
        {t('page:spotPrice.filter.dataSourceLink')}
        {/* 外部リンク icon は 12px (design は素の SVG 属性 width=12、
              `iconSize.externalLink`)。
              **旧実装は `chakra.svg` に `width="12"` を渡していて 48px で出ていた** —
              chakra の style prop は数値文字列を spacing token として解決するので
              `12` は 3rem になる (2026-08-31 デザイナー指摘「external アイコンが
              大きすぎる」)。 */}
        <FaArrowUpRightFromSquareRegular boxSize={iconSize.externalLink} />
      </Link>
    </Flex>
  )
}

type AreaCheckboxProps = {
  area: AreaDef
  checked: boolean
  onToggle: (code: string, checked: boolean) => void
}

const AreaCheckbox = ({
  area,
  checked,
  onToggle,
}: AreaCheckboxProps): React.ReactElement => (
  <Checkbox
    checked={checked}
    /**
     * checkbox 本体をそのエリアの系列色にする (= 凡例ドットの代わり)。 design は
     * 未チェックでも枠だけ色を付け、 チェックで塗りつぶす:
     * `.sm-area-chk-wrap input { border-color: var(--area-c) }` /
     * `:checked { background-color: var(--area-c); border-color: var(--area-c) }`
     * (`design/bidding/spot-price.html:25-31`)。
     *
     * **CSS 変数 (`--checkbox-color` 等) を Checkbox に渡す方式は効かない。**
     * DS `Checkbox` (chakra v3) は可視の箱を `[data-part="control"]` という**別要素**で
     * 描き、 native input は sr-only なので、 input 側に置いた変数は兄弟の control へ
     * 継承されない (`ForecastChartLegend` の JSDoc に 2026-08-12 の実 DOM 確認記録あり)。
     * 2026-08-31 デザイナー指摘「エリアの checkbox 背景色をグラフの系列色と同じに」は
     * 旧実装がこの方式で**全て DS 既定の青のまま**だったため。
     *
     * 同じ画面群で先に解決している `ForecastChartLegend` と同じく `css` prop で
     * control を直接指定する。 DS recipe は `@layer recipes` 内なので、 無 layer で
     * 出力される `css` prop が hover/active 派生ごと勝つ。 disabled は
     * `:not([data-disabled])` で除外し DS 既定のフォールバックに委ねる。
     */
    css={{
      '& [data-part="control"]:not([data-disabled])': {
        borderColor: area.color,
      },
      '& [data-part="control"][data-state="checked"]:not([data-disabled])': {
        background: area.color,
        borderColor: area.color,
      },
    }}
    onCheckedChange={(detail: { checked: boolean | 'indeterminate' }) => {
      onToggle(area.code, detail.checked === true)
    }}
  >
    {/* fontSize は指定しない: DS checkbox の label 補正 (`~/constants/system.ts`
        dsFallbackFixRecipes.checkbox) が design SoT の 14px を当てる。 */}
    <Text>{area.name}</Text>
  </Checkbox>
)
